import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import { FormattedMessage, useIntl } from 'react-intl'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { useMemo } from 'react'
import LoyaltyBenefitItemTile from 'loyalty/components/LoyaltyBenefitItemTile'
import { groupBy, sum } from 'lib/array/arrayUtils'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'

interface Props {
  id?: string
  views: Array<App.Checkout.LoyaltyLoungePassItemView>
  isSoldOut?: boolean
  onEdit: () => void
  onRedeem: () => void
}

function LoyaltyBenefitsLoungeItem({ id, views, onEdit, onRedeem, isSoldOut }: Props) {
  const intl = useIntl()
  const loungePassBenefit = useLoyaltyUserBenefitView('lounge_pass')
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const loungeImage: App.Image = useMemo(() => ({
    id: '6s83ysxhz3t606evbzz',
    title: intl.formatMessage({
      id: '28WhxfRPW8pXM1Flkqxi',
      defaultMessage: 'Lounge pass',
      description: 'Image title for the lounge pass loyalty benefit thumbnail',
    }),
  }), [intl])

  const heading = intl.formatMessage({
    id: 'zSPXAvw6egjQ1SuGwY-r',
    defaultMessage: 'Free airport lounge passes',
    description: 'Heading for lounge pass benefit tile in the loyalty benefits section',
  })
  const unlimitedAvailable = intl.formatMessage({
    id: 'TxD_052fXNtlmO16z2YA',
    defaultMessage: 'Unlimited available',
    description: 'Loyalty tier label when lounge passes are unlimited',
  })
  const countAvailable = (count: number) => intl.formatMessage(
    {
      id: 'TiFtyeMJv0s5yxCgDqb4',
      defaultMessage: '{count}x available',
      description: 'Loyalty tier label showing available lounge pass count',
    },
    { count },
  )
  const addedLabel = intl.formatMessage({
    id: '29JEJkclqqGDtVjZ_WQ9',
    defaultMessage: 'Added',
    description: 'Status label when lounge passes have been added in the loyalty program section',
  })
  const editButtonText = intl.formatMessage({
    id: 'cp4eOp-RVFNlSi_b_ff5',
    defaultMessage: 'Edit',
    description: 'Action button label to edit lounge pass selection in the loyalty program section',
  })
  const redeemButtonText = intl.formatMessage({
    id: 'siQz-Li3BzlL01zWnaW5',
    defaultMessage: 'Redeem',
    description: 'Action button label to redeem lounge passes in the loyalty program section',
  })
  const selectDateLaterText = useMemo(() => {
    const purchaseSynonym = config.brandConfig.purchaseSynonym
    if (purchaseSynonym === 'escape') {
      return intl.formatMessage({
        id: 'tDAULDYU-aVlFIMvfZkr',
        defaultMessage: 'You can select the date and time for your passes later via My Escapes.',
        description: 'Message about selecting lounge pass date and time later on escape-branded sites',
      })
    }
    if (purchaseSynonym === 'booking') {
      return intl.formatMessage({
        id: 'L5cdCYr-ULH0_FVJDHMN',
        defaultMessage: 'You can select the date and time for your passes later via My Bookings.',
        description: 'Message about selecting lounge pass date and time later on booking-branded sites',
      })
    }
    return intl.formatMessage({
      id: 'Ebxbvr8EGOC8U6cw8dJt',
      defaultMessage: 'You can select the date and time for your passes later via My Purchases.',
      description: 'Message about selecting lounge pass date and time later on purchase-branded sites',
    })
  }, [intl])
  const loungeDescription = intl.formatMessage({
    id: 'kd4VilIT3CXiI4OZbXM8',
    defaultMessage: 'Relax while you wait for your international flight in select airport lounges.',
    description: 'Description of airport lounge benefit in the flight search or booking flow',
  })

  const totalPrice = useMemo(() => sum(views, view => view.totals.price), [views])

  const groupedByExperience = useMemo(() => {
    const groups = groupBy(views, view => `${view.experience.id}:${view.item.date ?? ''}:${view.item.time ?? ''}`)
    return groups.values().map(experienceViews => {
      const { experience, item } = experienceViews[0]
      const ticketsSummary = groupBy(experienceViews, view => view.ticket.id)
        .values()
        .map(tickets => `${tickets.length}x ${tickets[0].ticket.name}`)
        .toArray()
        .join(', ')
      const airportLabel = experience.airport
        ? `${experience.airport.name} (${experience.airport.code})`
        : experience.name
      return {
        experienceId: experience.id,
        airportLabel,
        date: item.date,
        time: item.time,
        isBookLater: item.isBookLater,
        ticketsSummary,
      }
    }).toArray()
  }, [views])

  return <VerticalSpacer gap={2} id={id}>
    <LoyaltyBenefitItemTile
      id="airport-lounge"
      heading={heading}
      labels={<>
        {!!loungePassBenefit && <LoyaltyTierLabel kind="primary" tier={tier}>
          {loungePassBenefit.availability === Infinity ? unlimitedAvailable : countAvailable(loungePassBenefit.availability)}
        </LoyaltyTierLabel>}
        {!!views.length && <StatusLabel variant="confirmation" startIcon={<LineCheckCircleIcon />}>{addedLabel}</StatusLabel>}
      </>}
      image={loungeImage}
      actions={<>
        {!isSoldOut && !!views.length && <Group
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="center"
          tabletDirection="vertical"
          tabletHorizontalAlign="start"
          tabletVerticalAlign="end"
          gap={8}
        >
          {totalPrice > 0 && <PriceRowPrice size="M" price={totalPrice} saleUnit="total" />}
          <TextButton
            kind="secondary"
            startIcon={<LineEditAltIcon />}
            variant="default"
            onClick={onEdit}
            fit="desktop-full-width"
          >
            {editButtonText}
          </TextButton>
        </Group>}
        {!isSoldOut && !views.length && <TextButton kind="secondary" onClick={onRedeem}>
          {redeemButtonText}
        </TextButton>}
      </>}
    >
      {!!views.length && <VerticalSpacer gap={12}>
        {groupedByExperience.map(group => <VerticalSpacer gap={4} key={`${group.experienceId}:${group.date}:${group.time}`}>
          <BodyText variant="medium" weight="bold">{group.airportLabel}</BodyText>
          {!group.isBookLater && (group.date || group.time) && <BodyText variant="medium">
            {group.date && group.time && <FormattedMessage
              id="WxDM_kA-yoL-88QkZUZW"
              defaultMessage="{date}, {time}"
              description="Lounge pass booking date and time display in the booking flow"
              values={{
                date: <FormatDateTime value={group.date} format="dmy-casual" />,
                time: group.time,
              }}
            />}
            {group.date && !group.time && <FormatDateTime value={group.date} format="dmy-casual" />}
            {!group.date && group.time && group.time}
            {formatMiddleDotSeparator(intl)}{group.ticketsSummary}
          </BodyText>}
          {!group.isBookLater && !group.date && !group.time && <BodyText variant="medium">
            {group.ticketsSummary}
          </BodyText>}
          {group.isBookLater && <>
            <BodyText variant="medium">{group.ticketsSummary}</BodyText>
            <BodyText variant="medium" colour="highlight-primary-dark" weight="bold">
              {selectDateLaterText}
            </BodyText>
          </>}
        </VerticalSpacer>)}
      </VerticalSpacer>}
      {!views.length && <BodyText variant="medium">
        {loungeDescription}
      </BodyText>}
    </LoyaltyBenefitItemTile>
  </VerticalSpacer>
}

export default LoyaltyBenefitsLoungeItem
